import { useQuery, useQueryClient, UseQueryResult } from '@tanstack/react-query';
import { FirebaseError } from 'firebase/app';
import {
  collection, limit, onSnapshot, orderBy, query, Timestamp,
} from 'firebase/firestore';
import { useEffect } from 'react';

import { firstSnapshotFromCache } from '@/utils/firestoreFirstSnapshot';

import { db } from '../../../config';

/**
 * Log automatycznych przebudów bazy wiedzy agenta AI.
 * Backend zapisuje wpis przy każdej przebudowie: `hotels/{id}/aiagents/build-state/logs/{logId}`.
 */
export type KbBuildTrigger = 'auto' | 'agentConfig' | 'agentKnowledge' | 'manual';

export interface KbBuildLog {
  id: string;
  createdAt: number;
  ok: boolean;
  trigger: KbBuildTrigger;
  /** Sekcje bazy wiedzy, które się zmieniły (np. `products`, `faq`). */
  sections: string[];
  durationMs: number;
  error?: string;
}

const DEFAULT_LOGS_LIMIT = 5;

const toLog = (id: string, data: Record<string, unknown>): KbBuildLog => ({
  id,
  createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toMillis() : Date.now(),
  ok: data.ok === true,
  trigger: (typeof data.trigger === 'string' ? data.trigger : 'manual') as KbBuildTrigger,
  sections: Array.isArray(data.sections) ? data.sections.filter((s): s is string => typeof s === 'string') : [],
  durationMs: typeof data.durationMs === 'number' ? data.durationMs : 0,
  error: typeof data.error === 'string' ? data.error : undefined,
});

/** Ostatnie wpisy logu przebudów (nasłuch na żywo, najnowsze pierwsze). */
export const useKbBuildLogs = (
  organizationId: string | null | undefined,
  limitCount: number = DEFAULT_LOGS_LIMIT,
): UseQueryResult<KbBuildLog[], FirebaseError> => {
  const queryClient = useQueryClient();
  const queryKey = ['kb-build-logs', organizationId, limitCount] as const;

  useEffect(() => {
    if (!organizationId) return undefined;
    const logsQuery = query(
      collection(db, 'hotels', organizationId, 'aiagents', 'build-state', 'logs'),
      orderBy('createdAt', 'desc'),
      limit(limitCount),
    );
    const unsubscribe = onSnapshot(
      logsQuery,
      (snapshot) => {
        queryClient.setQueryData(queryKey, snapshot.docs.map((d) => toLog(d.id, d.data())));
      },
      (error) => {
        // eslint-disable-next-line no-console
        console.error('[useKbBuildLogs] error', error);
        queryClient.setQueryData(queryKey, []);
      },
    );
    return () => unsubscribe();
  }, [organizationId, limitCount, queryClient]);

  return useQuery<KbBuildLog[], FirebaseError>({
    queryKey,
    queryFn: ({ signal }) => firstSnapshotFromCache(
      queryClient,
      queryKey,
      organizationId
        ? query(
          collection(db, 'hotels', organizationId, 'aiagents', 'build-state', 'logs'),
          orderBy('createdAt', 'desc'),
          limit(limitCount),
        )
        : null,
      [],
      signal,
    ),
    enabled: !!organizationId,
    staleTime: Infinity,
    retry: 0,
  });
};
